// Error toast. Renders when `error` is non-empty. Manages its own
// exit animation (200ms), auto-dismiss (3s), and dismiss-on-
// pointerdown-or-Escape listeners. Parent owns the error string;
// the toast owns everything else.
//
// Extracted from ProjectRoomPage 2026-04-24 per Reef audit.
//
// Messages that look like "OpenRouter key missing" (428 /
// no_openrouter_key / "paste an OpenRouter key…") are surfaced as a
// linked warning rather than a raw danger string — tone the colour
// down from danger to warning, embed a Link to Settings → Provider
// keys so the next move is one click away. Anywhere the product
// tells the user "set your key" should point to the place to do it.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const KEY_MESSAGE_RE =
  /^(no[_ ]openrouter[_ ]key|paste an openrouter key|428|precondition)/i;

export function ErrorToast({
  error,
  onClear,
}: {
  error: string;
  onClear: () => void;
}) {
  const [exiting, setExiting] = useState(false);

  // Trigger the exit animation, then actually clear the error.
  const dismiss = useCallback(() => {
    setExiting((already) => {
      if (already) return already;
      window.setTimeout(() => {
        onClear();
        setExiting(false);
      }, 200);
      return true;
    });
  }, [onClear]);

  // Reset exiting flag whenever a new error lands so the in-animation
  // plays fresh.
  useEffect(() => {
    if (error) setExiting(false);
  }, [error]);

  // Dismiss listeners: pointerdown anywhere, Escape, auto 3s. A
  // zero-timeout arm guard prevents the pointerdown that produced
  // the error from dismissing it immediately.
  useEffect(() => {
    if (!error || exiting) return;
    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, 0);
    const auto = window.setTimeout(() => dismiss(), 3000);
    const onPointerDown = () => {
      if (armed) dismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(arm);
      window.clearTimeout(auto);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [error, exiting, dismiss]);

  if (!error) return null;

  const keyMissing = KEY_MESSAGE_RE.test(error);
  const toneBox = keyMissing
    ? "border-warning text-warning"
    : "border-danger text-danger";
  const toneButton = keyMissing
    ? "text-warning hover:bg-warning/15"
    : "text-danger hover:bg-danger/15";

  return (
    <div
      className="pointer-events-none absolute left-0 right-0 top-[5.75rem] z-40 flex justify-center px-4"
      aria-live="polite"
    >
      <div
        className={`pointer-events-auto flex max-w-full items-center gap-3 rounded-md border bg-surface-raised px-4 py-2 text-sm shadow-[var(--wt-shadow)] ${toneBox} ${
          exiting ? "wt-toast-out" : "wt-toast-in"
        }`}
        data-testid="project-error"
        role="alert"
      >
        {keyMissing ? (
          <Link
            to="?tab=settings#provider-keys"
            className="truncate cursor-pointer underline decoration-dotted underline-offset-2"
          >
            Add an OpenRouter key
          </Link>
        ) : (
          <span className="truncate">{error}</span>
        )}
        <button
          type="button"
          onClick={() => dismiss()}
          aria-label="Dismiss"
          className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors ${toneButton}`}
          data-testid="project-error-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
